/**
 * Zebra GZP-450 (o similar) via WebUSB.
 * Vendor ID Zebra: 0x0A5F. La impresora expone un endpoint BULK OUT que acepta ZPL.
 *
 * Uso:
 *   const printer = useZebraPrinter();
 *   await printer.connect();          // pide autorización al navegador
 *   await printer.print(zplCommand);  // manda ZPL raw
 *
 * Requiere HTTPS. Chrome / Edge / Opera. NO funciona en Firefox / Safari.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

const ZEBRA_VENDOR_ID = 0x0A5F;

export function useZebraPrinter() {
  const [device, setDevice] = useState(null);
  const [connecting, setConnecting] = useState(false);

  const supported = typeof navigator !== "undefined" && !!navigator.usb;

  // Al montar, intentar recuperar un device ya autorizado
  useEffect(() => {
    if (!supported) return;
    (async () => {
      try {
        const devs = await navigator.usb.getDevices();
        const zebra = devs.find((d) => d.vendorId === ZEBRA_VENDOR_ID);
        if (zebra) setDevice(zebra);
      } catch { /* silencioso */ }
    })();
  }, [supported]);

  const connect = useCallback(async () => {
    if (!supported) {
      toast.error("Este navegador no soporta WebUSB. Usá Chrome, Edge u Opera.");
      return null;
    }
    setConnecting(true);
    try {
      const dev = await navigator.usb.requestDevice({ filters: [{ vendorId: ZEBRA_VENDOR_ID }] });
      setDevice(dev);
      toast.success(`Impresora ${dev.productName || "Zebra"} conectada`);
      return dev;
    } catch (e) {
      if (e?.name === "NotFoundError") {
        toast.info("No se eligió ninguna impresora.");
      } else {
        toast.error("Error al conectar: " + (e?.message || e));
      }
      return null;
    } finally { setConnecting(false); }
  }, [supported]);

  const print = useCallback(async (zpl) => {
    if (!device) {
      toast.warning("Conectá primero la impresora Zebra.");
      return false;
    }
    try {
      if (!device.opened) await device.open();
      if (device.configuration === null) await device.selectConfiguration(1);
      // Buscar la primera interfaz con endpoint OUT
      const iface = device.configuration.interfaces.find((i) =>
        i.alternate.endpoints.some((e) => e.direction === "out")
      );
      if (!iface) throw new Error("No se encontró interfaz OUT en la impresora");
      await device.claimInterface(iface.interfaceNumber);
      const endpoint = iface.alternate.endpoints.find((e) => e.direction === "out");
      const encoder = new TextEncoder();
      await device.transferOut(endpoint.endpointNumber, encoder.encode(zpl));
      await device.releaseInterface(iface.interfaceNumber);
      toast.success("Etiqueta enviada");
      return true;
    } catch (e) {
      const msg = String(e?.message || e || "");
      // Windows suele bloquear WebUSB porque su driver nativo de Zebra reclamó
      // la interfaz. Sugerimos usar Zadig para instalar WinUSB.
      if (/access denied|acceso denegado|the device is used by another app|permission/i.test(msg)) {
        toast.error(
          "Windows tiene el driver Zebra reclamando el USB. Necesitás instalar WinUSB con Zadig — pedile a la asistencia el instructivo.",
          { duration: 8000 },
        );
      } else if (/unable to claim interface|already claimed/i.test(msg)) {
        toast.error(
          "La impresora está siendo usada por otro programa. Cerrá Zebra Setup Utilities o desconectá y volvé a conectar el USB.",
          { duration: 8000 },
        );
      } else {
        toast.error("Error al imprimir: " + msg);
      }
      return false;
    }
  }, [device]);

  return { supported, device, connecting, connect, print };
}

/**
 * Genera ZPL para una etiqueta 50x50mm con:
 *  - Nombre del producto + variante
 *  - Peso neto
 *  - Fecha elaboración + vencimiento
 *  - Nº de Lote (Juliano)
 *  - QR con URL de trazabilidad
 *  - Datos del frigorífico (razón social + CUIT)
 *
 * Densidad Zebra GZP-450: 203 dpi → 50mm = 400 dots.
 */
export function generarZplEtiqueta({
  nombre = "",
  variante = "",
  componente = "",  // combos: nombre del producto componente (se imprime SIEMPRE, aunque "variante" esté apagada)
  peso = "",
  bultos = null,  // cantidad de cajas/bultos físicos que contiene el lote
  fechaElab = "",
  fechaVenc = "",
  lote = "",
  qrUrl = "",
  frigorifico = "LA BIFERIA S.R.L.",
  cuitFrigorifico = "",
  copies = 1,
  config = null, // { textos, secciones, fuentes, layout } — desde AdminLabelsPage
}) {
  // Sanear caracteres para ZPL (evitar ^, ~ que son delimitadores)
  const clean = (s) => String(s || "").replace(/\^/g, " ").replace(/~/g, " ").trim();
  // Config defaults + merge
  const sec = (config?.secciones) || {};
  const txt = (config?.textos) || {};
  const fnt = (config?.fuentes) || {};
  const lyt = (config?.layout) || {};
  const pos = (config?.posiciones) || {};
  const showNombre     = sec.nombre !== false;
  const showVariante   = sec.variante !== false || !!clean(componente);
  const showPeso       = sec.peso !== false;
  const showElab       = sec.fecha_elab !== false;
  const showVenc       = sec.fecha_venc !== false;
  const showLote       = sec.lote !== false;
  const showQR         = sec.qr !== false;
  const showFrigo      = sec.frigorifico !== false;
  const showCuit       = sec.cuit !== false;
  const showSep1       = sec.separador_1 !== false;
  const showSep2       = sec.separador_2 !== false;
  const showLateral    = sec.linea_lateral !== false;
  const showLogo       = sec.logo === true && !!(config?.logo && config.logo.zpl_hex);
  const nombreH        = Number(fnt.nombre_h) || 32;
  const varianteH      = Number(fnt.variante_h) || 24;
  const pesoH          = Number(fnt.peso_h) || 38;
  const loteH          = Number(fnt.lote_h) || 32;
  const fechasH        = Number(fnt.fechas_h) || 20;
  const pw             = Number(lyt.ancho_dots) || 440;   // 55mm
  const ll             = Number(lyt.alto_dots) || 360;    // 45mm
  const frigoNombre    = (txt.frigorifico != null ? txt.frigorifico : frigorifico);
  const cuit           = (txt.cuit || cuitFrigorifico || "");
  const labelNeto      = txt.label_neto  || "NETO:";
  const labelElab      = txt.label_elab  || "Elab:";
  const labelVenc      = txt.label_venc  || "Venc:";
  const labelLote      = txt.label_lote  || "LOTE:";
  const labelBultos    = txt.label_bultos || "BULTOS:";

  // Defaults de posición (mismos que en label_templates.py) — se usan
  // cuando el usuario aún no configuró una pieza.
  const defPos = {
    logo:         { x: 8,   y: 8,   w: 80,  h: 60 },
    nombre:       { x: 96,  y: 8,   w: 336, h: 34 },
    variante:     { x: 96,  y: 44,  w: 336, h: 30 },
    separador_1:  { x: 8,   y: 78,  w: 424, h: 2 },
    peso:         { x: 8,   y: 88,  w: 424, h: 40 },
    fechas:       { x: 8,   y: 134, w: 424, h: 24 },
    lote:         { x: 8,   y: 162, w: 424, h: 34 },
    separador_2:  { x: 8,   y: 200, w: 424, h: 2 },
    qr:           { x: 8,   y: 208, w: 140, h: 140 },
    frigorifico:  { x: 160, y: 212, w: 272, h: 22 },
    cuit:         { x: 160, y: 238, w: 272, h: 20 },
    linea_lateral:{ x: 160, y: 262, w: 272, h: 60 },
  };
  const P = (k) => ({ ...(defPos[k] || { x: 8, y: 8, w: pw - 16, h: 20 }), ...(pos[k] || {}) });

  const lineaNombre = clean(nombre).toUpperCase();
  const lineaVariante = clean(componente || variante).toUpperCase();
  const lineaPeso = `${labelNeto} ${clean(peso)} kg`;
  const lineaBultos = (bultos != null && Number(bultos) > 0)
    ? `${labelBultos} ${Number(bultos)}`
    : "";
  const lineaElab = `${labelElab} ${clean(fechaElab)}`;
  const lineaVenc = `${labelVenc} ${clean(fechaVenc)}`;
  const lineaLote = `${labelLote} ${clean(lote)}`;
  const lineaFrig = clean(frigoNombre);
  const lineaCuit = cuit ? `CUIT ${clean(cuit)}` : "";

  const bloques = [];
  bloques.push(`^XA`);
  bloques.push(`^PW${pw}`);
  bloques.push(`^LL${ll}`);
  bloques.push(`^LH0,0`);
  bloques.push(`^CI28`);

  // Logo (^GFA)
  if (showLogo) {
    const p = P("logo");
    const total = Number(config.logo.total_bytes) || 0;
    const rowBytes = Number(config.logo.bytes_per_row) || 0;
    const hex = String(config.logo.zpl_hex || "");
    if (total > 0 && rowBytes > 0 && hex) {
      bloques.push(`^FO${p.x},${p.y}^GFA,${total},${total},${rowBytes},${hex}^FS`);
    }
  }

  if (showNombre) {
    const p = P("nombre");
    bloques.push(`^FO${p.x},${p.y}^A0N,${nombreH},${nombreH - 4}^FB${p.w},1,0,L^FD${lineaNombre}^FS`);
  }
  if (showVariante) {
    const p = P("variante");
    bloques.push(`^FO${p.x},${p.y}^A0N,${varianteH},${varianteH - 2}^FB${p.w},2,0,L^FD${lineaVariante}^FS`);
  }
  {
    if (showSep1) {
      const p = P("separador_1");
      bloques.push(`^FO${p.x},${p.y}^GB${p.w},${Math.max(2, p.h)},2^FS`);
    }
  }
  if (showPeso) {
    const p = P("peso");
    const linea = lineaBultos
      ? `${lineaPeso}   ${lineaBultos}`
      : lineaPeso;
    bloques.push(`^FO${p.x},${p.y}^A0N,${pesoH},${pesoH - 4}^FB${p.w},1,0,L^FD${linea}^FS`);
  }
  if (showElab || showVenc) {
    const p = P("fechas");
    const linea = [showElab ? lineaElab : "", showVenc ? lineaVenc : ""].filter(Boolean).join("   ");
    bloques.push(`^FO${p.x},${p.y}^A0N,${fechasH},${fechasH - 2}^FB${p.w},1,0,L^FD${linea}^FS`);
  }
  if (showLote) {
    const p = P("lote");
    bloques.push(`^FO${p.x},${p.y}^A0N,${loteH},${loteH - 4}^FB${p.w},1,0,L^FR^FD${lineaLote}^FS`);
  }
  {
    if (showSep2) {
      const p = P("separador_2");
      bloques.push(`^FO${p.x},${p.y}^GB${p.w},${Math.max(2, p.h)},2^FS`);
    }
  }
  if (showQR && qrUrl) {
    const p = P("qr");
    // QR magnification 4 ≈ 152 dots; el ancho configurado no cambia el módulo,
    // pero preservamos x/y del usuario.
    bloques.push(`^FO${p.x},${p.y}^BQN,2,4^FDLA,${clean(qrUrl)}^FS`);
  }
  if (showFrigo) {
    const p = P("frigorifico");
    bloques.push(`^FO${p.x},${p.y}^A0N,20,18^FB${p.w},1,0,L^FD${lineaFrig}^FS`);
  }
  if (showCuit && lineaCuit) {
    const p = P("cuit");
    bloques.push(`^FO${p.x},${p.y}^A0N,18,16^FB${p.w},1,0,L^FD${lineaCuit}^FS`);
  }
  // Compacto lateral con datos redundantes (fecha/lote/bultos)
  if (showLateral) {
    const lineasLat = [];
    if (showElab) lineasLat.push(`${labelElab} ${clean(fechaElab)}`);
    if (showVenc) lineasLat.push(`${labelVenc} ${clean(fechaVenc)}`);
    if (showLote) lineasLat.push(`${labelLote.replace(":", "").trim()}: ${clean(lote)}`);
    if (lineaBultos) lineasLat.push(lineaBultos);
    if (lineasLat.length > 0) {
      const p = P("linea_lateral");
      bloques.push(`^FO${p.x},${p.y}^A0N,16,14^FB${p.w},4,0,L^FD${lineasLat.join("\\&")}^FS`);
    }
  }

  // Cuadros de texto libres agregados por el usuario
  const customs = Array.isArray(config?.custom_texts) ? config.custom_texts : [];
  customs.forEach((ct) => {
    const x = Number(ct.x) || 0;
    const y = Number(ct.y) || 0;
    const w = Number(ct.w) || (pw - x - 8);
    const fh = Number(ct.font_h) || 24;
    const align = ["L", "C", "R"].includes(ct.align) ? ct.align : "L";
    const isBold = !!ct.bold;
    const isUnderline = !!ct.underline;
    const text = clean(ct.text || "");
    if (!text) return;
    // Texto principal — ^FB con justificación L/C/R
    bloques.push(`^FO${x},${y}^A0N,${fh},${Math.max(6, fh - 2)}^FB${w},2,0,${align},0^FD${text}^FS`);
    // "Bold" simulado: doble strike un dot a la derecha (ZPL no tiene bold real en A0).
    if (isBold) {
      bloques.push(`^FO${x + 1},${y}^A0N,${fh},${Math.max(6, fh - 2)}^FB${w},2,0,${align},0^FD${text}^FS`);
    }
    // Subrayado: línea horizontal justo debajo del texto (grosor = fh/10).
    if (isUnderline) {
      const uy = y + fh + 2;
      const thick = Math.max(2, Math.round(fh / 12));
      bloques.push(`^FO${x},${uy}^GB${w},${thick},${thick}^FS`);
    }
    // Cursiva: ZPL no soporta italic con la fuente A0. Se ignora en impresión
    // pero se muestra correctamente en la vista previa.
  });
  bloques.push(`^PQ${copies}`);
  bloques.push(`^XZ`);
  return bloques.join("\n");
}

export default useZebraPrinter;
